<template>
  <div class="meal-settings-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <el-button @click="$router.push('/dashboard')" type="primary" plain style="margin-right: 10px;">
            返回首页
          </el-button>
          <span>膳食内容管理</span>
          <el-button type="primary" @click="handleAdd">添加膳食</el-button>
        </div>
      </template>
      
      <el-table :data="mealList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="meal_type" label="餐食类型" width="120">
          <template #default="scope">
            {{ scope.row.meal_type === 1 ? '早餐' : scope.row.meal_type === 2 ? '午餐' : '晚餐' }}
          </template>
        </el-table-column>
        <el-table-column prop="meal_date" label="供应日期" width="120" />
        <el-table-column prop="menu" label="菜单内容" show-overflow-tooltip />
        <el-table-column prop="price" label="价格" width="120">
          <template #default="scope">
            ¥{{ scope.row.price }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="Number(scope.row.status) === 1 ? 'success' : 'danger'">
              {{ Number(scope.row.status) === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button 
              :type="Number(scope.row.status) === 1 ? 'danger' : 'success'" 
              size="small" 
              @click="handleStatusChange(scope.row)"
            >
              {{ Number(scope.row.status) === 1 ? '禁用' : '启用' }}
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="dialogType === 'add' ? '添加膳食' : '编辑膳食'" 
      v-model="dialogVisible"
      width="600px"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="餐食类型" prop="meal_type">
          <el-select v-model="form.meal_type">
            <el-option :value="1" label="早餐" />
            <el-option :value="2" label="午餐" />
            <el-option :value="3" label="晚餐" />
          </el-select>
        </el-form-item>
        <el-form-item label="供应日期" prop="meal_date">
          <el-date-picker
            v-model="form.meal_date"
            type="date"
            placeholder="选择日期"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item label="菜单内容" prop="menu">
          <el-input v-model="form.menu" type="textarea" :rows="4" />
        </el-form-item>
        <el-form-item label="价格" prop="price">
          <el-input-number v-model="form.price" :min="0" :precision="2" :step="0.1" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="form.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'MealSettings',
  setup() {
    const loading = ref(false)
    const mealList = ref([])
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const formRef = ref(null)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    
    const form = ref({
      meal_type: 1,
      meal_date: '',
      menu: '',
      price: 0,
      status: 1
    })
    
    const rules = {
      meal_type: [{ required: true, message: '请选择餐食类型', trigger: 'change' }],
      meal_date: [{ required: true, message: '请选择供应日期', trigger: 'change' }],
      menu: [{ required: true, message: '请输入菜单内容', trigger: 'blur' }],
      price: [{ required: true, message: '请输入价格', trigger: 'blur' }]
    }
    
    const fetchMealList = async () => {
      loading.value = true
      try {
        const response = await fetch(`/api/meal_settings_list.php?page=${currentPage.value}&pageSize=${pageSize.value}`)
        const data = await response.json()
        if (data.code === 0) {
          mealList.value = data.data.list
          total.value = data.data.total
        } else {
          ElMessage.error(data.msg || '获取膳食列表失败')
        }
      } catch (error) {
        console.error('获取膳食列表失败:', error)
        ElMessage.error('获取膳食列表失败')
      } finally {
        loading.value = false
      }
    }
    
    const handleAdd = () => {
      form.value = {
        meal_type: 1,
        meal_date: '',
        menu: '',
        price: 0,
        status: 1
      }
      dialogType.value = 'add'
      dialogVisible.value = true
    }
    
    const handleEdit = (row) => {
      form.value = { ...row }
      dialogType.value = 'edit'
      dialogVisible.value = true
    }
    
    const handleStatusChange = async (row) => {
      try {
        const action = Number(row.status) === 1 ? '禁用' : '启用'
        await ElMessageBox.confirm(`确定要${action}该膳食吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await fetch('/api/meal_settings_status.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            status: Number(row.status) === 1 ? 0 : 1
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(`${action}成功`)
          fetchMealList()
        } else {
          ElMessage.error(data.msg || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('更新状态失败:', error)
          ElMessage.error('更新状态失败')
        }
      }
    }
    
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定要删除该膳食吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await fetch('/api/meal_settings_delete.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success('删除成功')
          fetchMealList()
        } else {
          ElMessage.error(data.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }
    
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        
        const url = dialogType.value === 'add' ? '/api/meal_settings_add.php' : '/api/meal_settings_update.php'
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(form.value)
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(dialogType.value === 'add' ? '添加成功' : '更新成功')
          dialogVisible.value = false
          fetchMealList()
        } else {
          ElMessage.error(data.msg || (dialogType.value === 'add' ? '添加失败' : '更新失败'))
        }
      } catch (error) {
        console.error('表单提交失败:', error)
        ElMessage.error('表单验证失败')
      }
    }
    
    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchMealList()
    }
    
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchMealList()
    }
    
    onMounted(() => {
      fetchMealList()
    })
    
    return {
      loading,
      mealList,
      dialogVisible,
      dialogType,
      form,
      rules,
      formRef,
      currentPage,
      pageSize,
      total,
      handleAdd,
      handleEdit,
      handleStatusChange,
      handleDelete,
      handleSubmit,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.meal-settings-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style> 